import React, { useContext } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Link from '@atlaskit/link/link';
import { Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message/message';

import { RendererContext } from '../../context';

type EgressErrorMessageProps = {
	url: string;
};

const messages = defineMessages({
	pleaseUseThe: {
		id: 'forge-ui.egressErrorMessage.please-use-the',
		defaultMessage: 'Please use the',
		description: 'Use data image scheme for an image source message part 1',
	},
	dataImage: {
		id: 'forge-ui.egressErrorMessage.data-image',
		defaultMessage: 'data:image',
		description: 'Use data image scheme for an image source part 2',
	},
	schemeForAnImageSource: {
		id: 'forge-ui.egressErrorMessage.scheme-for-an-image-source',
		defaultMessage: 'scheme for an image source',
		description: 'Use data image scheme for an image source part 3',
	},
	egressPermissionsFor: {
		id: 'forge-ui.egressErrorMessage.egress-permissions-for',
		defaultMessage: 'Egress permissions for',
		description: 'Egress permissions missing in manifest message part 1',
	},
	missingInTheManifestForMoreInformation: {
		id: 'forge-ui.egressErrorMessage.missing-in-the-manifest-for-more-information',
		defaultMessage: 'missing in the manifest. For more information',
		description: 'Egress permissions missing in manifest message part 2',
	},
	seeReferenceDocs: {
		id: 'forge-ui.egressErrorMessage.see-reference-docs',
		defaultMessage: 'see reference docs',
		description: 'Egress permissions missing in manifest message part 3',
	},
	unableToDisplayDueToImageLinkIssue: {
		id: 'forge-ui.egressErrorMessage.unableToDisplayDueToImageLinkIssue',
		defaultMessage: 'Unable to display due to an image link issue.',
		description: 'Message for the egress error message',
	},
});

export const EgressErrorMessage = ({ url }: EgressErrorMessageProps): React.JSX.Element => {
	const { forgeEnvironment } = useContext(RendererContext);
	const dataUriRegex = /^data/;
	const isDataUri = dataUriRegex.test(url);

	const intl = useIntl();

	if (forgeEnvironment === 'DEVELOPMENT') {
		return (
			<SectionMessage appearance="error" testId="egress-error-message">
				{isDataUri ? (
					<Text>
						{intl.formatMessage(messages.pleaseUseThe)}{' '}
						<Text as="strong">{intl!.formatMessage(messages.dataImage)}</Text>{' '}
						{intl!.formatMessage(messages.schemeForAnImageSource)}
					</Text>
				) : (
					<Text>
						{intl!.formatMessage(messages.egressPermissionsFor)}{' '}
						<Link href={url} target="_blank">
							{url}
						</Link>{' '}
						{intl!.formatMessage(messages.missingInTheManifestForMoreInformation)}{' '}
						<Link
							href="https://developer.atlassian.com/platform/forge/manifest-reference/permissions/#images"
							target="_blank"
						>
							{intl!.formatMessage(messages.seeReferenceDocs)}
						</Link>
						.
					</Text>
				)}
			</SectionMessage>
		);
	}

	return (
		<SectionMessage appearance="error">
			{intl!.formatMessage(messages.unableToDisplayDueToImageLinkIssue)}
		</SectionMessage>
	);
};
